import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {AuthenticationService} from '../../shared/services/authentication.service';
import {DataTableColumn, DataTableComponent} from '../../shared/data-table/data-table.component';
import {LITE_LOGIN_ACCESSES_URI, LiteLoginService} from '../shared/lite-login.service';
import {LiteLoginSearchFilter} from '../shared/lite-login-search-filter.model';
import {Option} from '../../shared/data-filters/data-filter-select/data-filter-select.component';
import {LiteLoginAccessSource, LiteLoginAccessStatus, LiteLoginLoadInfo} from '../shared/lite-login-access.model';
import {Carrier} from '../../user-dashboard/shared/carrier.model';
import {catchError, finalize, tap} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {LoaderService} from '../../shared/services/loader.service';
import {CarrierService} from '../../user-dashboard/shared/carrier.service';
import {ErrorHandlerService} from '../../shared/services/error-handler.service';
import {EMPTY} from 'rxjs';
import {NavigationService} from '../../shared/services/navigation.service';
import {SortDirection} from '../../shared/sort-direction';
import {URL_BASE} from '../../core/constants';
import {ModalService} from '../../shared/services/modal.service';
import {TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';
import { NavigationLinkService } from '../../shared/services/navigation-link.service';

@Component({
  selector: 'app-lite-login-accesses-list',
  templateUrl: './lite-login-accesses-list.component.html',
  styleUrls: ['./lite-login-accesses-list.component.scss']
})
export class LiteLoginAccessesListComponent implements OnInit {

  canManageDrivers = this.authService.canManageDrivers();

  carriers: Carrier[] = [];
  carrier: Carrier;

  @ViewChild(DataTableComponent)
  accessTable: DataTableComponent;

  filters: LiteLoginSearchFilter = {
    emailAddress: null,
    filterNumber: null,
    createdAtAfter: null,
    createdAtBefore: null,
    status: null,
    carrierId: null,
  };
  statusOptions: Option[] = [
    { labelKey: 'lite-login.statuses.all', value: null },
    { labelKey: 'lite-login.statuses.in_use', value: LiteLoginAccessStatus.IN_USE },
    { labelKey: 'lite-login.statuses.activated', value: LiteLoginAccessStatus.ACTIVATED },
    { labelKey: 'lite-login.statuses.pending', value: LiteLoginAccessStatus.PENDING },
    { labelKey: 'lite-login.statuses.expired', value: LiteLoginAccessStatus.EXPIRED },
  ];
  sortColumn: DataTableColumn;
  SortDirection = SortDirection;

  LiteLoginAccessSource = LiteLoginAccessSource;
  LiteLoginAccessStatus = LiteLoginAccessStatus;

  columns: DataTableColumn[] = [];

  @ViewChild('userColumn', { static: true })
  userColumn: TemplateRef<any>;
  @ViewChild('loadColumn', { static: true })
  loadColumn: TemplateRef<any>;
  @ViewChild('creationColumn', { static: true })
  creationColumn: TemplateRef<any>;
  @ViewChild('availabilityColumn', { static: true })
  availabilityColumn: TemplateRef<any>;
  @ViewChild('useColumn', { static: true })
  useColumn: TemplateRef<any>;
  @ViewChild('statusColumn', { static: true })
  statusColumn: TemplateRef<any>;
  @ViewChild('actionsColumn', { static: true })
  actionsColumn: TemplateRef<any>;

  @ViewChild('confirmPopup', { static: true })
  confirmPopup: TemplateRef<any>;
  @ViewChild('confirmResendPopup', { static: true })
  confirmResendPopup: TemplateRef<any>;

  loadPage = (p: number, pSize: number, sortColumn: DataTableColumn, direction: SortDirection) =>
    this.liteLoginService.search(p, pSize, direction, this.filters)

  constructor(
    private authService: AuthenticationService,
    private loaderService: LoaderService,
    private liteLoginService: LiteLoginService,
    private carrierService: CarrierService,
    private errorHandlerService: ErrorHandlerService,
    private navigationService: NavigationService,
    public link: NavigationLinkService,
    private modalService: ModalService,
    private translate: TranslateService,
    private toastrService: ToastrService,
  ) {
    if (this.canManageDrivers) {
      this.loaderService.startLoad();
      this.carrierService.getSelfCarrier().pipe(
        tap(carrier => {
          this.carrier = carrier;
          this.filters.carrierId = carrier.id;
          localStorage.setItem('lite-login.carrierId', carrier.id);
        }),
        catchError((e: HttpErrorResponse) => {
          this.errorHandlerService.handleHttpError(e, 'lite-login.errors.fetch-carrier');
          return EMPTY;
        }),
        finalize(() => this.loaderService.stopLoad())
      ).subscribe()
    } else {
      this.loaderService.startLoad();
      this.carrierService.getAllLight().pipe(
        tap( carriers => {
          this.carriers = carriers;
          const savedCarrierId = localStorage.getItem('lite-login.carrierId');
          if (savedCarrierId) {
            this.carrier = carriers.find(carrier => carrier.id === savedCarrierId);
            this.filters.carrierId = savedCarrierId;
          }
        }),
        catchError((e: HttpErrorResponse) => {
          this.errorHandlerService.handleHttpError(e, 'lite-login.errors.fetch-carriers');
          return EMPTY;
        }),
        finalize(() => this.loaderService.stopLoad())
      ).subscribe()
    }
  }

  ngOnInit(): void {
    this.columns =  [
      {
        name: 'user',
        displayed: true,
        translationKey: 'lite-login.columns.user',
        template: this.userColumn,
        sortable: false
      }, {
        name: 'load',
        displayed: true,
        translationKey: 'lite-login.columns.load',
        template: this.loadColumn,
        sortable: false
      }, {
        name: 'creation',
        displayed: true,
        translationKey: 'lite-login.columns.creation-date',
        template: this.creationColumn,
        sortable: false
      }, {
        name: 'availability',
        displayed: true,
        translationKey: 'lite-login.columns.availability',
        template: this.availabilityColumn,
        sortable: false
      }, {
        name: 'use',
        displayed: true,
        translationKey: 'lite-login.columns.use-date',
        template: this.useColumn,
        sortable: false
      }, {
        name: 'status',
        displayed: true,
        translationKey: 'lite-login.columns.status',
        template: this.statusColumn,
        sortable: true,
      }, {
        name: 'actions',
        displayed: true,
        translationKey: 'lite-login.columns.actions',
        template: this.actionsColumn,
        alignRight: true,
        sortable: false,
        overflowVisible: true
      }
    ];
    this.sortColumn = this.columns.find(col => col.name === 'status');
  }

  modify(id: string) {
    this.navigationService.liteLogin().edit(id).go();
  }

  see(id: string) {
    this.navigationService.liteLogin().see(id).go();
  }

  delete(id: string, emailAddress: string, loadsInfo: LiteLoginLoadInfo[]) {
    this.modalService.open({
      title: this.translate.instant('lite-login.confirm.delete.title'),
      content: this.confirmPopup,
      data: {
        emailAddress: emailAddress,
        loadId: loadsInfo[0].loadId
      },
      validateButton: { text: this.translate.instant('lite-login.confirm.delete.confirm') }
    }).subscribe(() => {
      this.loaderService.startLoad();
      this.liteLoginService.delete(id)
        .pipe(
          finalize(() => {
            this.loaderService.stopLoad();
            this.accessTable.changePage(this.accessTable.page);
          })
      ).subscribe(
        () => this.toastrService.success(this.translate.instant('lite-login.success.delete', { emailAddress: emailAddress })),
        () => this.toastrService.error(this.translate.instant('lite-login.errors.delete'))
      );
    });
  }

  resendEmail(id: string, emailAddress: string) {
    this.modalService.open({
      title: this.translate.instant('lite-login.confirm.resend-mail.title'),
      content: this.confirmResendPopup,
      size: 'sm',
      data: { emailAddress: emailAddress },
      validateButton: { text: this.translate.instant('lite-login.confirm.resend-mail.confirm') }
    }).subscribe(() => {
      this.loaderService.startLoad();
      this.liteLoginService.resendEmail(id)
        .pipe(
          finalize(() => this.loaderService.stopLoad())
        ).subscribe(
        () => this.toastrService.success(this.translate.instant('lite-login.success.resend-mail')),
        () => this.toastrService.error(this.translate.instant('lite-login.errors.resend-mail'))
      );
    });
  }

  copyKey(id: string) {
    const access = this.accessTable.items.find(liteLoginAccess => liteLoginAccess.id === id);
    navigator.clipboard.writeText(access.liteAuthSecretCode)
      .then(
        () => this.toastrService.success(this.translate.instant('lite-login.success.copy')),
        () => this.toastrService.error(this.translate.instant('lite-login.error.copy'))
      );
  }

  openQr(id: string) {
    window.open(`${URL_BASE}/api/${LITE_LOGIN_ACCESSES_URI}/${id}/QRcode?format=png`, '_blank');
  }

  goToCreateAccess() {
    this.navigationService.liteLogin().create().go();
  }

  updateCarrier(carrier: Carrier) {
    this.carrier = carrier;
    this.filters.carrierId = carrier.id;
    localStorage.setItem('lite-login.carrierId', carrier.id);
    this.accessTable.changePage(this.accessTable.page);
  }

  resetFilters() {
    this.filters.carrierId = this.carrier.id;
  }
}
